import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import useMagnetic from '../hooks/useMagnetic';
import { scrollToSection } from '../hooks/useSmoothScroll';
import './Hero.css';

const STATS = [
  { to: 12,  suffix: '',   label: 'Years of Experience' },
  { to: 500, suffix: 'MW', label: 'Deployed' },
  { to: 99,  suffix: '%',  label: 'Uptime' },
  { to: 4,   suffix: '',   label: 'Cities' },
];

export default function Hero({ start }) {
  const titleRef  = useRef(null);
  const bottomRef = useRef(null);
  const descRef   = useRef(null);
  const scrollRef = useRef(null);
  const grainRef  = useRef(null);
  const videoRef  = useRef(null);
  const statsRef  = useRef(null);
  const btnRef    = useMagnetic(0.4);
  const played    = useRef(false);

  // Grain + video + parallax (runs once)
  useEffect(() => {
    const canvas = grainRef.current;
    const ctx = canvas.getContext('2d');
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener('resize', resize);
    let timer;
    const drawGrain = () => {
      // Skip the expensive pixel work while the hero is off-screen or the tab
      // is hidden — keeps the rest of the site smooth.
      const rect = canvas.getBoundingClientRect();
      const onScreen = !document.hidden && rect.bottom > 0 && rect.top < window.innerHeight;
      if (onScreen) {
        const img = ctx.createImageData(W, H);
        for (let i = 0; i < img.data.length; i += 4) {
          const v = Math.random() * 255 | 0;
          img.data[i] = img.data[i+1] = img.data[i+2] = v;
          img.data[i+3] = 18;
        }
        ctx.putImageData(img, 0, 0);
      }
      timer = setTimeout(drawGrain, onScreen ? 80 : 400);
    };
    drawGrain();

    const vid = videoRef.current;
    const showVid = () => { vid.style.opacity = '1'; };
    vid.addEventListener('canplay', showVid, { once: true });
    setTimeout(showVid, 3000);

    const onMove = e => {
      const dx = (e.clientX / window.innerWidth  - 0.5) * 10;
      const dy = (e.clientY / window.innerHeight - 0.5) * 6;
      if (titleRef.current) titleRef.current.style.transform = `translate(${dx}px,${dy}px)`;
    };
    window.addEventListener('mousemove', onMove);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      clearTimeout(timer);
    };
  }, []);

  // Intro reveal — gated on the preloader finishing
  useEffect(() => {
    if (!start || played.current) return;
    played.current = true;

    const words = titleRef.current.querySelectorAll('.word');
    const tl = gsap.timeline({ delay: 0.15 });
    tl.to(descRef.current,   { opacity: 1, duration: 0.7, ease: 'power2.out' })
      .to(words,             { y: '0%', duration: 1.4, stagger: 0.1, ease: 'power4.out' }, '-=0.4')
      .to(bottomRef.current, { opacity: 1, duration: 0.9, ease: 'power2.out' }, '-=0.8')
      .to(statsRef.current,  { opacity: 1, y: 0, duration: 0.9, ease: 'power2.out' }, '-=0.5')
      .to(scrollRef.current, { opacity: 1, duration: 0.7, ease: 'power2.out' }, '-=0.4');

    // Count-up stats
    statsRef.current.querySelectorAll('.stat-num').forEach(el => {
      const target = Number(el.dataset.to);
      const obj = { v: 0 };
      gsap.to(obj, {
        v: target, duration: 2, ease: 'power2.out', delay: 0.6,
        onUpdate: () => { el.firstChild.nodeValue = String(Math.round(obj.v)); },
      });
    });
  }, [start]);

  const scrollDown = () => scrollToSection('#about');

  return (
    <section className="section" id="home">
      <video ref={videoRef} className="hero-video" autoPlay muted loop playsInline>
        <source src="/Solar_panels.mp4" type="video/mp4" />
      </video>
      <div className="hero-overlay" />
      <canvas ref={grainRef} className="hero-grain" />

      {/* Orbiting sun graphic */}
      <div className="hero-orbit" aria-hidden="true">
        <div className="orbit-core" />
        <div className="orbit-ring orbit-ring-1"><span className="orbit-node" /></div>
        <div className="orbit-ring orbit-ring-2"><span className="orbit-node" /></div>
      </div>

      <div className="hero-content">
        <div className="hero-inner">
          <p ref={descRef} className="eyebrow hero-eyebrow">Next-generation solar energy</p>
          <h1 ref={titleRef} className="hero-title">
            <span className="line"><span className="word">Power</span></span>
            <span className="line"><span className="word">Your</span></span>
            <span className="line"><span className="word accent">World.</span></span>
          </h1>

          <div ref={bottomRef} className="hero-bottom">
            <div className="hero-rule" />
            <button className="btn" ref={btnRef} onClick={scrollDown}>
              <span>Explore</span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 6h10M6 1l5 5-5 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          <div ref={statsRef} className="hero-stats">
            {STATS.map((s, i) => (
              <div className="stat" key={i}>
                <div className="stat-num" data-to={s.to}>0<em>{s.suffix}</em></div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="scroll-track">
        <div className="scroll-line" />
        <span>Scroll</span>
      </div>

      <p className="year-badge">EST. 2024</p>
    </section>
  );
}
